// Library Methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import UserCard from "./UserCard/UserCard";
import ErrorText from "src/components/shared/text/ErrorText/ErrorText";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import AddUserModal from "./AddUserModal/AddUserModal";
import Alert from "src/components/shared/alerts/Alert/Alert";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import CustomIconButton from "src/components/shared/buttons/CustomIconButton.tsx/CustomIconButton";
import ExpandableSearchbar from "src/components/shared/inputs/ExpandableSearchbar/ExpandableSearchbar";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import { PlusIcon, TrashIcon } from "src/assets/icons";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import {
  CustomIconButtonDark,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import useDebounce from "src/hooks/input/useDebounce";
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useAddClinicUser } from "src/services/clinics/hooks/clinic-users/useAddClinicUser";
import { useAddAuth0User } from "src/services/clinics/hooks/clinic-users/useAddAuth0User";
import { useDeleteClinicUsers } from "src/services/clinics/hooks/clinic-users/useDeleteClinicUsers";
import { useClinicUsersData } from "src/services/clinics/hooks/clinic-users/useClinicUsersData";

// Types
import { ClinicUserType } from "src/services/clinics/types";
import { OrderType } from "src/types/sharedTypes";

// Utils
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import { isValidEmail } from "src/utils/text/textHelper";
import getUserFromToken from "src/utils/token/tokenHelper";

const MAX_NUM_OF_ROWS = 4;

const UserManagement = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();
  const { getToken } = useTokenAccess();

  const [currentUser, setCurrentUser] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<Array<string>>([]);
  const [page, setPage] = useState(1);

  const orderByEmail: Array<keyof ClinicUserType> = useMemo(
    () => ["email"],
    []
  );
  const [order, setOrder] = useState<OrderType>("asc");
  const [orderByOptions, setOrderByOptions] = useState<
    Array<keyof ClinicUserType>
  >([...orderByEmail]);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebounce(searchTerm);

  // Modal states
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const {
    data: usersData,
    isError: isErrorUsers,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    isRefetching: isRefetchingUsers,
  } = useClinicUsersData(clinicInfo?.id);

  const {
    mutate: addMutate,
    data: addedUser,
    isPending: isAddPending,
    isSuccess: isAddSuccess,
  } = useAddClinicUser(clinicInfo?.id);

  const {
    mutate: addAuth0Mutate,
    isPending: isAddAuth0Pending,
    isSuccess: isAddAuth0Success,
  } = useAddAuth0User(clinicInfo?.id);

  const { mutate: deleteMutate, isPending: isDeletePending } =
    useDeleteClinicUsers(clinicInfo?.id);

  const isLoading = useMemo(
    () =>
      isLoadingUsers ||
      isFetchingUsers ||
      isRefetchingUsers ||
      isAddPending ||
      isAddAuth0Pending ||
      isDeletePending,
    [
      isLoadingUsers,
      isFetchingUsers,
      isRefetchingUsers,
      isAddPending,
      isAddAuth0Pending,
      isDeletePending,
    ]
  );

  // Table header values
  const headCells = useMemo(
    () => [
      {
        id: "checkbox",
        gridSize: 1,
        orderUsing: [],
        isCheckbox: true,
      },
      {
        id: "email",
        label: t("Email"),
        gridSize: 11,
        orderUsing: [...orderByEmail],
      },
    ],
    [t, orderByEmail]
  );

  const hasUsers = useMemo(
    () => !isLoading && !isErrorUsers && usersData && usersData?.length > 0,
    [isLoading, isErrorUsers, usersData]
  );

  const hasNoUsers = useMemo(
    () => !isLoading && !isErrorUsers && !usersData?.length,
    [isLoading, isErrorUsers, usersData]
  );

  // Filtered rows
  const filteredRows = useMemo(() => {
    let filterArray = usersData?.slice()?.filter((row) => {
      const rowHasSearchTerm =
        debouncedTerm?.length &&
        row?.email?.toLowerCase()?.indexOf(debouncedTerm?.toLowerCase()) < 0;

      return !rowHasSearchTerm;
    });

    return multiKeyCompare<ClinicUserType>(
      filterArray ?? [],
      order === "asc",
      orderByOptions
    );
  }, [usersData, debouncedTerm, order, orderByOptions]);

  // Rows to display, based on the current page
  const rowsOnCurrentPage = useMemo(() => {
    return getItemsToRender(filteredRows, page, MAX_NUM_OF_ROWS);
  }, [filteredRows, page]);

  const hasNoSearchResults = useMemo(
    () => debouncedTerm?.length > 0 && !filteredRows?.length,
    [debouncedTerm, filteredRows]
  );

  // Pagination change
  const onPaginationChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  // Handles selecting all checkboxes
  const handleSelectAllClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const isChecked = (event.target as HTMLInputElement).checked;

    // select all
    if (isChecked) {
      const newSelecteds = filteredRows.map((row) => row.email);
      setSelectedUsers([...newSelecteds]);
      return;
    }
    // deselect all
    else {
      setSelectedUsers([]);
    }
  };

  // Sort user cards by the table header value
  const handleRequestSort = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement>,
      property: Array<keyof ClinicUserType>
    ) => {
      const isAsc =
        orderByOptions.length &&
        orderByOptions[0] === property[0] &&
        order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderByOptions(property);
    },
    [order, orderByOptions]
  );

  // Handle checkbox clicks
  const handleCheckboxClick = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string
  ) => {
    const selectedIndex = selectedUsers.indexOf(id);
    let newSelected: Array<string> = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedUsers, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedUsers.slice(1));
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelected = newSelected.concat(selectedUsers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedUsers.slice(0, selectedIndex),
        selectedUsers.slice(selectedIndex + 1)
      );
    }

    setSelectedUsers(newSelected);
  };

  // Searchbar change
  const onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (page > 1) setPage(1);
    setSearchTerm(event?.target?.value);
  };

  // Clear searchbar
  const clearSearchFilter = () => {
    setSearchTerm("");
  };

  // Handle adding a new user
  const addUser = useCallback(
    (newUser: ClinicUserType) => {
      if (!isValidEmail(newUser.email)) throw new Error();

      // add user and authorize at the same time
      addMutate(newUser);
      addAuth0Mutate([newUser.email], {
        onSuccess: () => setOpenAddModal(false),
      });
    },
    [addMutate, addAuth0Mutate]
  );

  // Handles deleting a single user or multiple users at once
  const deleteUsers = useCallback(() => {
    // only allow a user to delete users other than themself
    const emailsToDelete = selectedUsers.filter((user) => user !== currentUser);

    deleteMutate(emailsToDelete, {
      onSuccess: () => {
        setSelectedUsers([]);
        setOpenDeleteModal(false);
      },
    });
  }, [deleteMutate, setSelectedUsers, selectedUsers, currentUser]);

  // After adding a new user, show the user's page
  useEffect(() => {
    if (addedUser && isAddSuccess && isAddAuth0Success) {
      // Find the index of the new user in the filteredRows array
      const selectedIndex = filteredRows.findIndex(
        (row) => row.email === addedUser?.email
      );
      if (selectedIndex < 0) return;

      // Calculate the page number
      const selectedPage =
        selectedIndex >= 0
          ? Math.ceil((selectedIndex + 1) / MAX_NUM_OF_ROWS)
          : 1;

      setPage(selectedPage);
    }
  }, [addedUser, isAddSuccess, isAddAuth0Success, filteredRows, setPage]);

  // get the email of the user that is currently logged in
  const getCurrentLoggedInUser = useCallback(async () => {
    const token = await getToken();
    const { userEmail } = getUserFromToken(token);

    setCurrentUser(userEmail);
  }, [getToken]);

  // get the current user when the page is loaded
  useEffect(() => {
    getCurrentLoggedInUser();
  }, [getCurrentLoggedInUser]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{ textTransform: "uppercase" }}
          fontSize="16px"
          color={mutedText}
        >
          {t("Clinic Users")}
        </Typography>
        <Box display="flex" justifyContent="flex-end" gap="8px">
          <ExpandableSearchbar
            id="user-management-searchbar"
            isLoading={isLoading}
            searchTerm={searchTerm}
            searchTooltip={t("Search Users")}
            onChange={onSearchChange}
            onClear={clearSearchFilter}
          />
          <CustomIconButton
            isLoading={isLoading}
            tooltipTitle={t("Add User")}
            onClick={() => setOpenAddModal(true)}
            icon={<PlusIcon />}
          />
          {selectedUsers?.length > 0 && (
            <CustomIconButton
              isLoading={isLoading}
              variant="error"
              tooltipTitle={t("Delete User")}
              onClick={() => setOpenDeleteModal(true)}
              icon={<TrashIcon />}
            />
          )}
        </Box>
      </Box>

      {/* Loading skeletons */}
      {isLoading && <TableSkeleton numOfRows={MAX_NUM_OF_ROWS} />}

      {hasUsers && !hasNoSearchResults && (
        <Box display="flex" flexDirection="column" gap="12px" mt="16px">
          <TableHeader<ClinicUserType>
            headCells={headCells}
            numSelected={selectedUsers?.length}
            order={order}
            orderBy={orderByOptions?.[0] ?? orderByEmail}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={filteredRows?.length}
          />
          {rowsOnCurrentPage?.map((rowData) => (
            <UserCard
              key={rowData.email}
              user={rowData}
              isSelectedForViewing={false}
              isSelectedForDeletion={
                selectedUsers?.indexOf(rowData.email) !== -1
              }
              handleCheckboxClick={handleCheckboxClick}
            />
          ))}
          <Box mt="12px">
            <CustomPagination
              page={page}
              count={getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS)}
              onChange={onPaginationChange}
            />
          </Box>
        </Box>
      )}

      {hasNoUsers && (
        <ErrorText
          header={t("No Users Found.")}
          message={
            <Typography maxWidth="400px">
              {t("Click the")}{" "}
              <IconButton
                sx={{
                  ...CustomIconButtonDark(theme),
                  width: `${UI_SIZES.iconButtonSize}px`,
                  height: `${UI_SIZES.iconButtonSize}px`,
                  pointerEvents: "none",
                }}
              >
                <PlusIcon />
              </IconButton>{" "}
              {t("button above to create a user.")}
            </Typography>
          }
        />
      )}
      {hasNoSearchResults && (
        <ErrorText
          header={t("No Users Found.")}
          message={t("Change your search term to see more results.")}
        />
      )}
      {isErrorUsers && (
        <ErrorText
          header={t("Something Went Wrong.")}
          message={t(
            "Your users could not be fetched. Please refresh the page or try again later."
          )}
        />
      )}

      {/* Add Clinic User modal */}
      <AddUserModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onConfirm={addUser}
        usersData={usersData ?? []}
      />

      {/* Delete Clinic User modal */}
      <SharedModal
        open={openDeleteModal}
        title={t("Delete User")}
        modalDescription={
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {selectedUsers?.includes(currentUser) && (
              <Alert
                variant="info"
                alertContent={t("You cannot delete your own user account.")}
              />
            )}
            {t("Are you sure you want to delete the selected user?", {
              count: selectedUsers?.length,
            })}
          </Box>
        }
        isDisabledButton={
          selectedUsers?.length === 1 && selectedUsers?.includes(currentUser)
        }
        confirmButtonText={t("Delete")}
        confirmButtonVariant="error"
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={deleteUsers}
      />
    </>
  );
};

export default UserManagement;
